import { useQuery } from '@tanstack/react-query';
import { getSmartSendOrderData as getSmartSendOrderDataAPI } from '../services/getSmartSendOrderData.api';

/**
 * Custom hook to fetch SmartSend order data with smart retry logic
 * @param {string} orderId - The order ID to fetch
 * @param {string} tokenId - Optional auth token (provided by consumer app via URL params, null for web G-links)
 * @returns {Object} React Query result with orderData, isLoading, isError, error
 */
export function useSmartSendQuery(orderId, tokenId) {
  return useQuery({
    queryKey: ['smartSendOrder', orderId],
    queryFn: async () => {
      if (!orderId) {
        throw new Error('Missing required parameter: orderId');
      }
      return getSmartSendOrderDataAPI(orderId, tokenId);
    },
    enabled: !!orderId, // Only fetch if orderId exists
    retry: (failureCount, error) => {
      // Don't retry on 429 (rate limit) errors
      if (error?.message?.includes('429')) {
        console.warn('Rate limit hit (429). Not retrying to avoid further throttling.');
        return false;
      }
      // Retry once for other errors
      return failureCount < 1;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 1s, 2s, 4s, etc.
      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
  });
}
